import BackgroundHeaderArc from 'common/BackgroundHeaderArcNew'
import styles from './index.module.scss'
import credentials from 'common/ProviderCredentials/credentials'
import Link from 'next/link'
import _isEmpty from 'lodash/isEmpty'
import { sessionStorageKeys } from 'constants/common'
import { getProviderProfileRoute } from 'dataSource/ProviderSearch/routes'
import { formatUserQuery, getStringifiedQuery } from 'utils/common'
import { useRouter } from 'next/router'
import useBreakpoint from 'hooks/useBreakPoint'
import { DEFAULT_PROFILE_IMAGE_PATH } from 'common/ProviderPreview/constants'
import Image from 'next/legacy/image'
import classNames from 'classnames'
import { getProviderTitle } from 'app/ProviderDetails/utils'
import { useMemo, useContext } from 'react'
import { TimeZoneContext } from 'context/common/TimeZoneContext'
import { dateFormats } from 'constants/common'
import { getTimeZoneFormattedDate } from 'utils/common'
import { slotAvailabilityLabels } from 'app/Bookings/constants'

type SlotGridHeaderProps = {
  providerDetails: Provider
  selectedSlot?: SelectedSlot
  appointmentMode?: string
  showShadow: boolean
  showSlotAndSessionInfo?: boolean
  showProviderCreds?: boolean
  showFloralsBehindTheImage: boolean
}

const SlotGridHeader: React.FC<SlotGridHeaderProps> = ({
  providerDetails,
  selectedSlot,
  appointmentMode = '',
  showShadow,
  showSlotAndSessionInfo = false,
  showProviderCreds = true,
  showFloralsBehindTheImage = false,
}) => {
  const {
    title,
    firstName,
    lastName,
    profileImagePath,
    pronouns,
    filters,
    practice,
  } = providerDetails
  const providerTitle = title || getProviderTitle(providerDetails)
  const providerCredentials = credentials[providerTitle] || {}
  const providerName = firstName + ' ' + lastName
  const providerProfileLink = getProviderProfileRoute(providerDetails)
  const { query } = useRouter()
  const formattedQuery = formatUserQuery(query)
  const timeZoneInSession = sessionStorage.getItem(
    sessionStorageKeys.TIMEZONE_ABBR
  )
  const queryObject = {
    ...(query?.insuranceId && { insuranceId: query.insuranceId }),
    ...(practice?.distanceInMiles && {
      distanceInMiles: practice.distanceInMiles.toFixed(2),
    }),
    ...(!_isEmpty(formattedQuery?.counselingType) && {
      counselingType: formattedQuery.counselingType,
    }),
    ...(!_isEmpty(formattedQuery?.availabilityType) && {
      availabilityType: formattedQuery.availabilityType[0],
    }),
    ...(timeZoneInSession && {
      tzAbbr: timeZoneInSession,
    }),
  }
  const { isSmallSize } = useBreakpoint()

  const genderPronounValue = useMemo(() => {
    // On Provider Search, providerDetails has "pronouns" property that has gender pronoun value
    // On Provider Profile, providerDetails has "filters" property that has gender pronoun value
    const capitalizedPronouns = pronouns?.map((pronoun) =>
      pronoun
        .split('/')
        .map((each) => each.charAt(0).toUpperCase() + each.slice(1))
        .join('/')
    )

    const genderPronouns = filters
      ?.filter((filter) => filter.type === 'gender_pronoun')
      .map((genderPronoun) => genderPronoun.value)
      .join('/')

    return capitalizedPronouns || genderPronouns
  }, [pronouns, filters])

  const { DAY_FORMAT, MONTH_DAY_FORMAT, TIME_WITH_TIMEZONE } = dateFormats
  const timeZoneFormattedDate = `${DAY_FORMAT} | ${MONTH_DAY_FORMAT} — ${TIME_WITH_TIMEZONE}`
  const { timeZone } = useContext(TimeZoneContext)

  return (
    <>
      <div tabIndex={-1} className={styles.SlotGridHeader}>
        <BackgroundHeaderArc showShadow={showShadow} />
        <Link
          href={{
            pathname: providerProfileLink,
            query: getStringifiedQuery(queryObject),
          }}
          className={styles.SlotGridHeader_linkContainer}
        >
          <div className={styles.SlotGridHeader_container}>
            <div className={styles.SlotGridHeader_container_image}>
              {showFloralsBehindTheImage ? (
                <>
                  <img
                    src="/images/provider-background-illustration-leaf.svg"
                    height={isSmallSize ? 87 : 166}
                    width={isSmallSize ? 126 : 180}
                    className={
                      styles.SlotGridHeader_container_image_providerBackgroundIllustration
                    }
                    alt="Provider background illustration"
                  />

                  <div
                    className={
                      styles.SlotGridHeader_container_image_subContainer
                    }
                  >
                    <img
                      src={
                        profileImagePath
                          ? profileImagePath
                          : DEFAULT_PROFILE_IMAGE_PATH
                      }
                      alt={`${providerName}'s profile` + ' image'}
                      className={
                        styles.SlotGridHeader_container_image_subContainer_providerImage
                      }
                    />
                  </div>
                </>
              ) : (
                <div
                  className={
                    styles.SlotGridHeader_container_image_subContainer_providerImage
                  }
                >
                  <Image
                    unoptimized={!!profileImagePath}
                    src={
                      profileImagePath
                        ? profileImagePath
                        : DEFAULT_PROFILE_IMAGE_PATH
                    }
                    layout="fill"
                    alt={`${providerName}'s profile image`}
                    className="rounded-circle"
                    sizes="100vw"
                    objectPosition="top"
                    objectFit="cover"
                  />
                </div>
              )}
            </div>

            <div
              className={classNames(styles.SlotGridHeader_container_text, {
                [styles.add_extra_margin]: showFloralsBehindTheImage,
              })}
            >
              <div
                className={styles.SlotGridHeader_container_text_subContainer}
              >
                <h3>
                  <span
                    className={
                      styles.SlotGridHeader_container_text_subContainer_name
                    }
                  >
                    {providerName}{' '}
                  </span>
                  {!!genderPronounValue && (
                    <span
                      className={
                        styles.SlotGridHeader_container_text_subContainer_pronouns
                      }
                    >
                      {genderPronounValue}
                    </span>
                  )}
                </h3>
              </div>
              {showSlotAndSessionInfo && (
                <div>
                  {selectedSlot?.slot && (
                    <div className="fs-14 font-weight-medium text-black">
                      {getTimeZoneFormattedDate(
                        new Date(selectedSlot.slot.start),
                        timeZoneFormattedDate,
                        timeZone
                      )}
                    </div>
                  )}
                  <div className="fs-14 text-secondary">
                    {`Session with ${providerDetails.firstName} ${providerDetails.lastName}`}
                    {appointmentMode && (
                      <span> | {slotAvailabilityLabels[appointmentMode]}</span>
                    )}
                  </div>
                </div>
              )}
              {showProviderCreds && (
                <div>
                  <h2 className={styles.SlotGridHeader_container_text_title}>
                    {providerCredentials.definition}
                  </h2>
                </div>
              )}
            </div>
          </div>
        </Link>
      </div>
    </>
  )
}

export default SlotGridHeader
